import React, { useState } from 'react';

import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { loyaltySubTabsContainerSx } from '../loyaltyTabStyles';

import ConciergeTab from './ConciergeTab';
import CreateInfiniteAccountTab from './CreateInfiniteAccountTab';

type ParentTabKey = 'create-account' | 'concierge';

/**
 * Top-level Infinite page. Hosts two parent sub-tabs:
 *  - Create account: register a new LE customer + enrol at Infinite in one flow.
 *  - Concierge: existing Assignments + Representatives experience.
 */
export default function InfiniteConciergePage() {
  const [tab, setTab] = useState<ParentTabKey>('create-account');

  return (
    <PermissionedComponent
      requiredRoles={[ROLE_ADMIN_USER, ROLE_LUX_LOYALTY_ADMIN]}
      fallback={
        <Typography color="text.secondary">
          You don&apos;t have permission to manage Infinite assignments.
        </Typography>
      }
    >
      <Box
        sx={(theme) => ({
          mb: 2,
          px: { xs: 2.5, md: 3 },
          py: 1.5,
          borderRadius: 2,
          color: 'common.white',
          background: theme.palette.loyaltyTiers.infinite.gradient,
          boxShadow: theme.shadows[1],
          '& .MuiTypography-root': { color: 'common.white' },
          '& .MuiSvgIcon-root': { color: 'common.white' },
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <AllInclusiveIcon sx={{ fontSize: 22 }} />
          <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.75 }}>
            Société · Top tier
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            Infinite
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            · Onboard members and manage the dedicated concierge programme
          </Typography>
        </Stack>
      </Box>

      <Box sx={loyaltySubTabsContainerSx}>
        <Tabs value={tab} onChange={(_, next) => setTab(next as ParentTabKey)}>
          <Tab value="create-account" label="Create account" />
          <Tab value="concierge" label="Concierge" />
        </Tabs>
      </Box>

      {tab === 'create-account' && <CreateInfiniteAccountTab />}
      {tab === 'concierge' && <ConciergeTab />}
    </PermissionedComponent>
  );
}
